/* ---------------------------------------------------------------------- */
/*                             Path resolution                             */
/* ---------------------------------------------------------------------- */
/**
 * Split a profiled path into the part addressing the row array and the part inside each row.
 *
 * `data[].inbound.open` → array at `data`, field `inbound.open`.
 * `belowReorderPoint`   → no array; a scalar on the response root.
 */
function splitRowPath(path) {
    const marker = path.indexOf('[]');
    if (marker === -1)
        return null;
    return {
        arrayPath: path.slice(0, marker),
        fieldPath: path.slice(marker + 2).replace(/^\./, ''),
    };
}
function readDeep(source, dotted) {
    if (!dotted)
        return source;
    let current = source;
    for (const segment of dotted.split('.')) {
        if (current === null || current === undefined || typeof current !== 'object') {
            return undefined;
        }
        current = current[segment];
    }
    return current;
}
function toNumber(value) {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}
/* ---------------------------------------------------------------------- */
/*                                Filters                                  */
/* ---------------------------------------------------------------------- */
function compare(actual, test) {
    if (test.isNull === true)
        return actual === null || actual === undefined;
    if (test.isNotNull === true)
        return actual !== null && actual !== undefined;
    if (test.eq !== undefined)
        return actual === test.eq;
    if (test.neq !== undefined)
        return actual !== test.neq;
    if (test.in !== undefined) {
        return test.in.some((candidate) => candidate === actual);
    }
    const numeric = toNumber(actual);
    // A null never satisfies a numeric bound. Cover is null wherever the run rate is zero, and
    // treating that as 0 would file every no-demand SKU into the most critical band.
    if (numeric === null)
        return false;
    if (test.lt !== undefined && !(numeric < test.lt))
        return false;
    if (test.lte !== undefined && !(numeric <= test.lte))
        return false;
    if (test.gt !== undefined && !(numeric > test.gt))
        return false;
    if (test.gte !== undefined && !(numeric >= test.gte))
        return false;
    return true;
}
function matches(row, filter, ctx) {
    if ('all' in filter)
        return filter.all.every((f) => matches(row, f, ctx));
    if ('any' in filter)
        return filter.any.some((f) => matches(row, f, ctx));
    if ('not' in filter)
        return !matches(row, filter.not, ctx);
    return compare(readRowField(row, filter.path, ctx), filter);
}
function readRowField(row, path, ctx) {
    const split = splitRowPath(path);
    if (!split) {
        // A scalar path used in a row predicate — read it off the response root.
        return ctx.readScalar(path);
    }
    return readDeep(row, split.fieldPath);
}
/* ---------------------------------------------------------------------- */
/*                              Aggregation                                */
/* ---------------------------------------------------------------------- */
function percentile(sorted, p) {
    if (sorted.length === 0)
        return null;
    const idx = (sorted.length - 1) * p;
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    if (lo === hi)
        return sorted[lo];
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}
function aggregate(rows, agg, path, ctx) {
    if (agg === 'count')
        return rows.length;
    if (!path)
        return rows.length;
    if (agg === 'countDistinct') {
        const seen = new Set();
        for (const row of rows)
            seen.add(readRowField(row, path, ctx));
        return seen.size;
    }
    // Nulls are skipped, never coerced to zero — a missing reading is not a reading of nothing.
    const values = [];
    for (const row of rows) {
        const n = toNumber(readRowField(row, path, ctx));
        if (n !== null)
            values.push(n);
    }
    if (values.length === 0)
        return agg === 'sum' ? 0 : null;
    switch (agg) {
        case 'sum':
            return values.reduce((a, b) => a + b, 0);
        case 'mean':
            return values.reduce((a, b) => a + b, 0) / values.length;
        case 'min':
            return Math.min(...values);
        case 'max':
            return Math.max(...values);
        case 'median':
            return percentile([...values].sort((a, b) => a - b), 0.5);
        case 'p10':
            return percentile([...values].sort((a, b) => a - b), 0.1);
        case 'p25':
            return percentile([...values].sort((a, b) => a - b), 0.25);
        case 'p75':
            return percentile([...values].sort((a, b) => a - b), 0.75);
        case 'p90':
            return percentile([...values].sort((a, b) => a - b), 0.9);
        default:
            return null;
    }
}
/**
 * Division that refuses to invent an answer.
 *
 * A zero or null denominator yields null, not Infinity and not zero. This is the single most
 * consequential line in the file: cover is `stock / rate`, and a SKU with stock and no demand
 * must read as "—", never as "∞ days" and never as "0 days" — the first is noise, the second
 * would rank it as the most urgent thing in the business.
 */
function divide(numerator, denominator) {
    if (numerator === null || denominator === null || denominator === 0)
        return null;
    return numerator / denominator;
}
/* ---------------------------------------------------------------------- */
/*                             Group metrics                               */
/* ---------------------------------------------------------------------- */
function evalGroupMetric(rows, metric, ctx, allRows) {
    switch (metric.kind) {
        case 'agg':
            return aggregate(rows, metric.agg, metric.path, ctx);
        case 'divide':
            return divide(evalGroupMetric(rows, metric.numerator, ctx, allRows), evalGroupMetric(rows, metric.denominator, ctx, allRows));
        case 'share': {
            const part = evalGroupMetric(rows, metric.of, ctx, allRows);
            const whole = evalGroupMetric(allRows ?? ctx.rows, metric.of, ctx, allRows);
            return divide(part, whole);
        }
        default:
            return null;
    }
}
function groupRows(rows, by, ctx) {
    const buckets = new Map();
    for (const row of rows) {
        const raw = readRowField(row, by, ctx);
        const key = raw === null || raw === undefined || raw === '' ? '—' : String(raw);
        const bucket = buckets.get(key);
        if (bucket)
            bucket.push(row);
        else
            buckets.set(key, [row]);
    }
    return [...buckets.entries()].map(([key, groupedRows]) => ({ key, rows: groupedRows }));
}
/* ---------------------------------------------------------------------- */
/*                            Value selectors                              */
/* ---------------------------------------------------------------------- */
function evalValue(selector, ctx) {
    switch (selector.kind) {
        case 'literal':
            return selector.value;
        case 'scalar':
            return toNumber(ctx.readScalar(selector.path));
        case 'count': {
            const rows = selector.where
                ? ctx.rows.filter((r) => matches(r, selector.where, ctx))
                : ctx.rows;
            return rows.length;
        }
        case 'agg': {
            const rows = selector.where
                ? ctx.rows.filter((r) => matches(r, selector.where, ctx))
                : ctx.rows;
            return aggregate(rows, selector.agg, selector.path, ctx);
        }
        case 'countGroups': {
            const groups = groupRows(ctx.rows, selector.by, ctx);
            let count = 0;
            for (const group of groups) {
                const value = evalGroupMetric(group.rows, selector.metric, ctx);
                // `$metric` is the reserved path meaning "the group metric just computed".
                if (compare(value, { ...selector.where, path: '$metric' }))
                    count += 1;
            }
            return count;
        }
        case 'divide':
            return divide(evalValue(selector.numerator, ctx), evalValue(selector.denominator, ctx));
        default:
            return null;
    }
}
/* ---------------------------------------------------------------------- */
/*                              Formatting                                 */
/* ---------------------------------------------------------------------- */
/** Indian shortening — 1,84,320 → 1.84 L. Used only when `compact` is set. */
function compactIndian(value) {
    const abs = Math.abs(value);
    if (abs >= 1e7)
        return `${(value / 1e7).toFixed(2)} Cr`;
    if (abs >= 1e5)
        return `${(value / 1e5).toFixed(2)} L`;
    if (abs >= 1e3)
        return `${(value / 1e3).toFixed(1)} K`;
    return String(Math.round(value));
}
function groupIndian(value, decimals) {
    const fixed = Math.abs(value).toFixed(decimals);
    const [whole, fraction] = fixed.split('.');
    // Last three digits, then pairs — 1,84,320 rather than 184,320.
    const head = whole.slice(0, -3);
    const tail = whole.slice(-3);
    const grouped = head
        ? `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`
        : tail;
    const sign = value < 0 ? '-' : '';
    return fraction ? `${sign}${grouped}.${fraction}` : `${sign}${grouped}`;
}
export function formatValue(value, format) {
    if (value === null)
        return '—';
    const unit = format?.unit;
    if (unit === '%') {
        // A share arrives as a fraction; a figure already in percent arrives as itself.
        const asPercent = Math.abs(value) <= 1 ? value * 100 : value;
        return `${asPercent.toFixed(format?.decimals ?? 1)}%`;
    }
    const decimals = format?.decimals ?? 0;
    const body = format?.compact
        ? compactIndian(value)
        : groupIndian(value, decimals);
    if (!unit)
        return body;
    if (unit === '₹')
        return `₹${body}`;
    return `${body} ${unit}`;
}
/**
 * Format a cell that may hold either a label or a number.
 *
 * Numbers format; non-empty strings pass through as themselves; absent values render as a dash.
 * Shared by the row-level columns of `nestedTable` and `rowTable`, which both mix the two —
 * a product name sits beside a shortfall in the same row.
 */
function formatCellValue(raw, format) {
    const numeric = toNumber(raw);
    if (numeric !== null)
        return { raw: numeric, formatted: formatValue(numeric, format) };
    if (raw === null || raw === undefined || raw === '')
        return { raw: null, formatted: '—' };
    return { raw: null, formatted: String(raw) };
}
function resolveTone(rules, value, share) {
    if (!rules?.length)
        return undefined;
    for (const rule of rules) {
        const subject = rule.when.path === '$share' ? share : value;
        if (compare(subject, rule.when))
            return rule.as;
    }
    return undefined;
}
/* ---------------------------------------------------------------------- */
/*                            Block evaluation                             */
/* ---------------------------------------------------------------------- */
function makeValue(selector, ctx, format, tone, shareOf) {
    const raw = evalValue(selector, ctx);
    const share = shareOf ? divide(raw, evalValue(shareOf, ctx)) : null;
    return {
        value: {
            raw,
            formatted: formatValue(raw, format),
            tone: resolveTone(tone, raw, share),
        },
        share,
    };
}
function evalKpis(block, ctx) {
    return {
        type: 'kpis',
        title: block.title,
        columns: block.columns ?? Math.min(block.items.length, 4),
        items: block.items.map((item) => {
            const { value, share } = makeValue(item.value, ctx, item.format, item.tone, item.shareOf);
            return {
                label: item.label,
                value,
                helper: item.helper,
                share: share === null ? undefined : formatValue(share, { unit: '%' }),
                compare: item.compareTo
                    ? {
                        label: item.compareTo.label,
                        value: makeValue(item.compareTo.value, ctx, item.format).value,
                    }
                    : undefined,
            };
        }),
    };
}
function evalGroupTable(block, ctx) {
    const scoped = block.where
        ? ctx.rows.filter((r) => matches(r, block.where, ctx))
        : ctx.rows;
    const groups = groupRows(scoped, block.by, ctx).map((group) => ({
        ...group,
        rank: evalGroupMetric(group.rows, block.rankBy, ctx, scoped),
    }));
    // Groups with no rankable value sort last regardless of direction — an absent answer is
    // never the best answer or the worst one.
    groups.sort((a, b) => {
        if (a.rank === null && b.rank === null)
            return 0;
        if (a.rank === null)
            return 1;
        if (b.rank === null)
            return -1;
        return block.direction === 'asc' ? a.rank - b.rank : b.rank - a.rank;
    });
    const kept = groups.slice(0, block.limit);
    const rest = groups.slice(block.limit);
    const toRow = (key, rows) => ({
        key,
        cells: block.columns.map((column) => ({
            label: column.label,
            value: {
                raw: evalGroupMetric(rows, column.metric, ctx, scoped),
                formatted: formatValue(evalGroupMetric(rows, column.metric, ctx, scoped), column.format),
            },
        })),
    });
    const rows = kept.map((group) => toRow(group.key, group.rows));
    if (block.includeOther && rest.length > 0) {
        rows.push(toRow(`Other (${rest.length})`, rest.flatMap((g) => g.rows)));
    }
    return {
        type: 'table',
        variant: 'group',
        title: block.title,
        headers: [block.by.replace(/^.*\[\]\./, ''), ...block.columns.map((c) => c.label)],
        rows,
        totalRow: block.includeTotal ? toRow('Total', scoped) : undefined,
    };
}
function evalBandTable(block, ctx) {
    const scoped = block.where
        ? ctx.rows.filter((r) => matches(r, block.where, ctx))
        : ctx.rows;
    const bands = [];
    const labels = block.labels ??
        block.edges
            .map((edge, i) => (i === 0 ? `0–${edge}` : `${block.edges[i - 1]}–${edge}`))
            .concat(`${block.edges[block.edges.length - 1]}+`);
    for (const label of labels)
        bands.push({ label, rows: [] });
    const nullRows = [];
    for (const row of scoped) {
        const value = toNumber(readRowField(row, block.path, ctx));
        if (value === null) {
            nullRows.push(row);
        }
        else {
            // Bands are half-open: a value equal to an edge belongs to the band above it, so
            // exactly 15 days of cover reads as "15–45", never as "7–15".
            const index = block.edges.findIndex((edge) => value < edge);
            bands[index === -1 ? bands.length - 1 : index].rows.push(row);
        }
    }
    if (block.nulls === 'ownBand' && nullRows.length > 0) {
        bands.push({ label: 'No demand', rows: nullRows });
    }
    // Share denominator excludes nulls when they are excluded, so the column totals 100%.
    const denominator = block.nulls === 'ownBand' ? scoped.length : scoped.length - nullRows.length;
    return {
        type: 'table',
        variant: 'band',
        title: block.title,
        headers: ['Band', 'SKUs', 'Share', ...(block.columns ?? []).map((c) => c.label)],
        rows: bands.map((band) => ({
            key: band.label,
            cells: [
                {
                    label: 'SKUs',
                    value: {
                        raw: band.rows.length,
                        formatted: formatValue(band.rows.length),
                    },
                },
                {
                    label: 'Share',
                    value: {
                        raw: divide(band.rows.length, denominator),
                        formatted: formatValue(divide(band.rows.length, denominator), {
                            unit: '%',
                        }),
                    },
                },
                ...(block.columns ?? []).map((column) => ({
                    label: column.label,
                    value: {
                        raw: evalGroupMetric(band.rows, column.metric, ctx, scoped),
                        formatted: formatValue(evalGroupMetric(band.rows, column.metric, ctx, scoped), column.format),
                    },
                })),
            ],
        })),
    };
}
function evalNestedTable(block, ctx) {
    const groups = groupRows(ctx.rows, block.by, ctx)
        .map((group) => ({
        ...group,
        rank: evalGroupMetric(group.rows, block.rankGroupsBy, ctx, ctx.rows),
    }))
        .sort((a, b) => (b.rank ?? -Infinity) - (a.rank ?? -Infinity))
        .slice(0, block.groupLimit);
    return {
        type: 'table',
        variant: 'nested',
        title: block.title,
        headers: [block.by.replace(/^.*\[\]\./, ''), ...block.groupColumns.map((c) => c.label)],
        rows: groups.map((group) => {
            const ranked = [...group.rows]
                .map((row) => ({
                row,
                score: evalValue(block.rankRowsBy, {
                    ...ctx,
                    rows: [row],
                }),
            }))
                .sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity))
                .slice(0, block.rowLimit);
            return {
                key: group.key,
                cells: block.groupColumns.map((column) => ({
                    label: column.label,
                    value: {
                        raw: evalGroupMetric(group.rows, column.metric, ctx, ctx.rows),
                        formatted: formatValue(evalGroupMetric(group.rows, column.metric, ctx, ctx.rows), column.format),
                    },
                })),
                children: ranked.map(({ row }, i) => ({
                    key: `${group.key}:${i}`,
                    cells: block.rowColumns.map((column) => ({
                        label: column.label,
                        value: formatCellValue(readRowField(row, column.path, ctx), column.format),
                    })),
                })),
            };
        }),
    };
}
function evalRowTable(block, ctx) {
    const scoped = block.where
        ? ctx.rows.filter((r) => matches(r, block.where, ctx))
        : ctx.rows;
    const sorted = [...scoped].sort((a, b) => {
        const av = toNumber(readRowField(a, block.orderBy.path, ctx));
        const bv = toNumber(readRowField(b, block.orderBy.path, ctx));
        if (av === null && bv === null)
            return 0;
        if (av === null)
            return 1;
        if (bv === null)
            return -1;
        return block.orderBy.direction === 'asc' ? av - bv : bv - av;
    });
    return {
        type: 'table',
        variant: 'row',
        title: block.title,
        headers: block.columns.map((c) => c.label),
        rows: sorted.slice(0, block.limit).map((row, i) => ({
            key: String(i),
            cells: block.columns.map((column) => ({
                label: column.label,
                value: formatCellValue(readRowField(row, column.path, ctx), column.format),
            })),
        })),
    };
}
function evalChart(block, ctx) {
    let categories;
    if (block.bands) {
        const { path, edges, labels } = block.bands;
        const names = labels ??
            edges
                .map((e, i) => (i === 0 ? `0–${e}` : `${edges[i - 1]}–${e}`))
                .concat(`${edges[edges.length - 1]}+`);
        categories = names.map((key) => ({ key, rows: [] }));
        for (const row of ctx.rows) {
            const value = toNumber(readRowField(row, path, ctx));
            if (value !== null) {
                const index = edges.findIndex((edge) => value < edge);
                categories[index === -1 ? categories.length - 1 : index].rows.push(row);
            }
        }
    }
    else if (block.by) {
        categories = groupRows(ctx.rows, block.by, ctx);
        if (block.orderBy === 'label') {
            categories.sort((a, b) => a.key.localeCompare(b.key));
        }
        else {
            const first = block.series[0];
            categories.sort((a, b) => (evalGroupMetric(b.rows, first.metric, ctx, ctx.rows) ?? -Infinity) -
                (evalGroupMetric(a.rows, first.metric, ctx, ctx.rows) ?? -Infinity));
        }
        if (block.limit)
            categories = categories.slice(0, block.limit);
    }
    else {
        categories = [];
    }
    return {
        type: 'chart',
        title: block.title,
        chart: block.chart,
        categories: categories.map((c) => c.key),
        series: block.series.map((series) => ({
            label: series.label,
            values: categories.map((c) => evalGroupMetric(c.rows, series.metric, ctx, ctx.rows)),
        })),
    };
}
function evalCallout(block, ctx) {
    const { value, share } = makeValue(block.value, ctx, block.format, block.tone);
    return {
        type: 'callout',
        text: block.template.replace('{value}', value.formatted),
        tone: resolveTone(block.tone, value.raw, share),
    };
}
/* ---------------------------------------------------------------------- */
/*                                 Entry                                   */
/* ---------------------------------------------------------------------- */
export function evaluateSummary(spec, sources) {
    const response = sources[spec.source];
    const missing = new Set();
    // Every block addresses rows through the same array, discovered from the first row-scoped
    // path the spec uses. A spec with no row paths at all still resolves its scalars.
    const rowArrayPath = findRowArrayPath(spec);
    const rows = rowArrayPath === null ? [] : asArray(readDeep(response, rowArrayPath));
    const ctx = {
        rows,
        readScalar: (path) => {
            const value = readDeep(response, path);
            if (value === undefined)
                missing.add(path);
            return value;
        },
        noteMissing: (path) => missing.add(path),
    };
    const blocks = [];
    for (const block of spec.blocks) {
        blocks.push(evalBlock(block, ctx));
    }
    return { blocks, missingPaths: [...missing], rowCount: rows.length };
}
function evalBlock(block, ctx) {
    switch (block.type) {
        case 'heading':
            return { type: 'heading', text: block.text, subtext: block.subtext };
        case 'callout':
            return evalCallout(block, ctx);
        case 'kpis':
            return evalKpis(block, ctx);
        case 'groupTable':
            return evalGroupTable(block, ctx);
        case 'bandTable':
            return evalBandTable(block, ctx);
        case 'nestedTable':
            return evalNestedTable(block, ctx);
        case 'rowTable':
            return evalRowTable(block, ctx);
        case 'chart':
            return evalChart(block, ctx);
        case 'narrative':
            return {
                type: 'narrative',
                instruction: block.instruction,
                style: block.style,
                count: block.count,
            };
        default:
            throw new Error(`Unknown summary block: ${block.type}`);
    }
}
function asArray(value) {
    return Array.isArray(value) ? value : [];
}
/** The array path every row-scoped selector in the spec shares. */
function findRowArrayPath(spec) {
    let found = null;
    const visit = (node) => {
        if (found !== null || node === null || typeof node !== 'object')
            return;
        if (Array.isArray(node)) {
            for (const item of node)
                visit(item);
            return;
        }
        for (const [key, value] of Object.entries(node)) {
            if (found !== null)
                return;
            if ((key === 'path' || key === 'by') &&
                typeof value === 'string' &&
                value.includes('[]')) {
                found = splitRowPath(value).arrayPath;
                return;
            }
            visit(value);
        }
    };
    visit(spec.blocks);
    return found;
}
//# sourceMappingURL=evaluate-summary.js.map